import { ReactNode } from "react"
import { Box, IconButton, Link, Tabs } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowUpRightFromSquare, faXmark } from "@fortawesome/pro-light-svg-icons"
import { makeStyles } from "tss-react/mui"
// Hidden for now (Task 260) — restore the stat cards and the "Order" tab label with these:
// import { Typography } from "@mui/material"
// import { faTruck, faUtensils } from "@fortawesome/pro-light-svg-icons"
// import { ProductionStatCard } from "./ProductionStatCard"
import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../../shared/components/pageHeader/PageHeaderApi"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../../shared/components/links/StandardBreadcrumbs"
import { StandardTab } from "../../../../../../../shared/components/tab/StandardTab"
import { StandardButton } from "../../../../../../../shared/components/buttons/StandardButton"
import { MERCHANT_REPORTS_ROUTE } from "../../../../../../../constants/routes"

import { ProductionGroupBy } from "../helpers"

const useStyles = makeStyles()((theme) => ({
    breadcrumbRow: {
        display: "flex",
        alignItems: "center",
        gap: "1rem",
    },
    fullscreenLink: {
        display: "inline-flex",
        alignItems: "center",
        gap: ".375rem",
        cursor: "pointer",
        whiteSpace: "nowrap",
        color: "#2196F3",
    },
    fullscreenIcon: {
        fontSize: "1rem",
    },
    fullscreenText: {
        fontWeight: 700,
        fontSize: ".875rem",
        lineHeight: 1.2,
        letterSpacing: 0,
    },
    statCards: {
        display: "flex",
        alignItems: "center",
        gap: "1rem",
        flexWrap: "wrap",
        [theme.breakpoints.down("md")]: {
            marginTop: "1rem",
        },
    },
    tabsRow: {
        display: "flex",
        alignItems: "center",
        paddingBottom: theme.spacing(3),
    },
    tabs: {
        minHeight: 36,
        "& .MuiTabs-flexContainer": {
            minHeight: 36,
        },
    },
    tab: {
        fontWeight: 500,
        fontSize: "0.875rem",
        color: theme.palette.text.secondary,
        backgroundColor: "transparent",
        borderRadius: "2.25rem",
        minHeight: 36,
        minWidth: "auto",
        padding: theme.spacing(0, 2),
        marginRight: theme.spacing(1),
        whiteSpace: "nowrap",
        "&.Mui-selected": {
            color: `${theme.palette.common.white} !important`,
            backgroundColor: theme.palette.tailwind.slate[900],
        },
        "&.Mui-selected:hover": {
            backgroundColor: theme.palette.tailwind.slate[700],
        },
    },
    notApprovedButton: {
        marginLeft: "auto",
        flexShrink: 0,
        whiteSpace: "nowrap",
        "&.MuiButton-root": {
            height: 36,
            borderRadius: "2.25rem",
            fontSize: ".875rem",
        },
    },
    fullscreenCloseButton: {
        marginLeft: ".5rem",
        alignSelf: "center",
        width: "3rem",
        height: "3rem",
        color: theme.palette.tailwind.slate[700],
    },
    orderLabel: {
        display: "inline-flex",
        alignItems: "center",
        flexShrink: 0,
        minHeight: "3rem",
        padding: "0 1rem",
        fontWeight: 500,
        fontSize: ".875rem",
        color: theme.palette.text.secondary,
        whiteSpace: "nowrap",
    },
}))

type Props = Readonly<{
    children: ReactNode
    groupBy: ProductionGroupBy
    onGroupByChange: (value: ProductionGroupBy) => void
    isFullscreen: boolean
    notApprovedView: boolean
    notApprovedCount: number
    onNotApprovedChange: (notApprovedView: boolean) => void
    onEnterFullscreen: () => void
    onExitFullscreen: () => void
    // Hidden for now (Task 260) — restore with the stat cards:
    // orderStats: Readonly<{ total: number; delivered: number }>
}>

export function ProductionViewHeader({
    children,
    groupBy,
    onGroupByChange,
    isFullscreen,
    notApprovedView,
    notApprovedCount,
    onNotApprovedChange,
    onEnterFullscreen,
    onExitFullscreen,
}: Props) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()

    const groupingIndex = groupBy === ProductionGroupBy.LOCATION ? 1 : 0

    const tabsValue = notApprovedView ? false : groupingIndex

    const tabItems = [
        {
            id: "time",
            label: l10n.getString("meeting-service-production-view-tab-delivery-time"),
            onSelect: () => {
                onNotApprovedChange(false)
                onGroupByChange(ProductionGroupBy.TIME)
            },
        },
        {
            id: "location",
            label: l10n.getString("meeting-service-production-view-tab-delivery-location"),
            onSelect: () => {
                onNotApprovedChange(false)
                onGroupByChange(ProductionGroupBy.LOCATION)
            },
        },
    ]

    // Hidden for now (Task 260) — stat cards (Tilbereder / Leveret); restore the `orderStats` prop + imports above,
    // and pass rightElement={statCards} to the PageHeader below.
    // const statCards = (
    //     <Box className={classes.statCards}>
    //         <ProductionStatCard
    //             icon={faUtensils}
    //             label={l10n.getString("meeting-service-production-view-stat-preparing")}
    //             value={orderStats.total}
    //             total={orderStats.total}
    //         />
    //         <ProductionStatCard
    //             icon={faTruck}
    //             label={l10n.getString("meeting-service-production-view-stat-delivered")}
    //             value={orderStats.delivered}
    //             total={orderStats.total}
    //         />
    //     </Box>
    // )

    return (
        <>
            {!isFullscreen && (
                <LocalizedStrict id="production-meeting-service-page-header" attrs={{ title: true }}>
                    <PageHeader page={Page.PRODUCTION_MEETING_SERVICE} title="Meeting service" alignItems="flex-start">
                        <Box className={classes.breadcrumbRow}>
                            <StandardBreadcrumbs>
                                <LocalizedStrict id="merchant-nav-item-home">
                                    <BreadcrumbLink to={MERCHANT_REPORTS_ROUTE}>Home</BreadcrumbLink>
                                </LocalizedStrict>
                                <LocalizedStrict id="merchant-production">
                                    <Breadcrumb>Production</Breadcrumb>
                                </LocalizedStrict>
                                <LocalizedStrict id="production-meeting-service-breadcrumb">
                                    <Breadcrumb>Meeting service</Breadcrumb>
                                </LocalizedStrict>
                            </StandardBreadcrumbs>
                            <Link className={classes.fullscreenLink} underline="hover" onClick={onEnterFullscreen}>
                                <FontAwesomeIcon icon={faArrowUpRightFromSquare} className={classes.fullscreenIcon} />
                                <LocalizedStrict id="meeting-service-production-view-fullscreen-link">
                                    <span className={classes.fullscreenText}>Fullscreen Mode</span>
                                </LocalizedStrict>
                            </Link>
                        </Box>
                    </PageHeader>
                </LocalizedStrict>
            )}

            <Box className={classes.tabsRow}>
                {/* Hidden for now (Task 260) — "Order" tab label (was an alternative grouping):
                <LocalizedStrict id="meeting-service-production-view-tab-order">
                    <Typography component="span" className={classes.orderLabel}>
                        Order
                    </Typography>
                </LocalizedStrict> */}
                <Tabs
                    className={classes.tabs}
                    value={tabsValue}
                    onChange={(_, newValue) => tabItems[newValue]?.onSelect()}
                    TabIndicatorProps={{ style: { display: "none" } }}
                >
                    {tabItems.map((tab) => (
                        <StandardTab key={tab.id} className={classes.tab} label={tab.label} disableRipple />
                    ))}
                </Tabs>
                <LocalizedStrict
                    id="meeting-service-production-view-tab-not-approved"
                    vars={{ count: notApprovedCount }}
                >
                    <StandardButton
                        variant={notApprovedView ? "contained" : "outlined"}
                        color="black"
                        size="small"
                        fullWidth={false}
                        className={classes.notApprovedButton}
                        onClick={() => onNotApprovedChange(!notApprovedView)}
                    >
                        Not approved ({notApprovedCount})
                    </StandardButton>
                </LocalizedStrict>
                {isFullscreen && (
                    <IconButton
                        className={classes.fullscreenCloseButton}
                        aria-label={l10n.getString("meeting-service-production-view-fullscreen-close")}
                        onClick={onExitFullscreen}
                    >
                        <FontAwesomeIcon icon={faXmark} />
                    </IconButton>
                )}
            </Box>

            {children}
        </>
    )
}
